// scheduling/components/dashboard/OverviewView.jsx
// Presentational: manager Overview — Needs Attention alerts, Upcoming 4 Weeks,
// Recent Notifications + the manager stat bar. Extracted from ManagerDashboard's
// overview tab. Shared job data arrives via props; alerts/upcoming are derived here.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BRAND, JOB_TYPE_COLORS } from '../../utils/schedConstants';
import { computeJobStats } from '../../utils/managerJobData';
import SchedStatBar from '../SchedStatBar';

export default function OverviewView({
  myJobs, enrichedJobs, wipByJob, conflicts, schedule, notifications, meStaffId,
  setActiveView, setSelectedJobId,
}) {
  const navigate = useNavigate();
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [alertFilter, setAlertFilter] = useState('all'); // all | conflict | budget | due
  const [conflictsNext3moOnly, setConflictsNext3moOnly] = useState(true);

  const recentNotifs = useMemo(() => (notifications || []).slice(0, 8), [notifications]);
  const stats = useMemo(() => computeJobStats(myJobs), [myJobs]);

  const alerts = useMemo(() => {
    const items = [];
    const now = new Date();
    for (const j of myJobs) {
      // A no-budget job with nothing scheduled still warrants the due-soon alert.
      const nothingScheduled = j.schedStatus === 'unscheduled' || (j.schedStatus === 'nobudget' && !(j.scheduledHrs > 0));
      if (nothingScheduled && j.Deadline) {
        const due = new Date(j.Deadline.slice(0, 10) + 'T12:00:00');
        const daysUntil = Math.ceil((due - now) / 86400000);
        if (daysUntil <= 30 && daysUntil > 0) {
          items.push({ type: 'due', severity: daysUntil <= 7 ? 'high' : daysUntil <= 14 ? 'medium' : 'low',
            title: `${j.ClientName} — ${j.Name}`, text: `Due in ${daysUntil} days, not yet scheduled`, jobId: j.JobId,
            dueDate: j.Deadline });
        }
      }
      const wip = wipByJob[j.JobId]?.total || 0;
      if (j.BudgetHours > 0 && (wip + j.scheduledHrs) > j.BudgetHours) {
        const overBy = Math.round((wip + j.scheduledHrs - j.BudgetHours) * 100) / 100;
        items.push({ type: 'budget', severity: 'medium',
          title: `${j.ClientName} — ${j.Name}`,
          text: `Over budget by ${overBy}h (${Math.round(wip)}h WIP + ${Math.round(j.scheduledHrs * 100) / 100}h scheduled / ${j.BudgetHours}h budget)`,
          jobId: j.JobId });
      }
    }
    const myStaffIds = new Set();
    for (const e of schedule) {
      const job = myJobs.find(j => j.JobId === e.JobId);
      if (job) myStaffIds.add(e.StaffId);
    }
    const curMon = new Date();
    curMon.setDate(curMon.getDate() - (curMon.getDay() === 0 ? 6 : curMon.getDay() - 1));
    curMon.setHours(0, 0, 0, 0);
    const currentWeekMondayISO = `${curMon.getFullYear()}-${String(curMon.getMonth() + 1).padStart(2, '0')}-${String(curMon.getDate()).padStart(2, '0')}`;
    const cutoff = new Date(curMon); cutoff.setMonth(cutoff.getMonth() + 3);
    const threeMonthCutoffISO = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}-${String(cutoff.getDate()).padStart(2, '0')}`;
    for (const c of conflicts) {
      if (!myStaffIds.has(c.StaffId)) continue;
      const weekStartISO = new Date(c.WeekStartDate).toISOString().slice(0, 10);
      if (weekStartISO < currentWeekMondayISO) continue;
      if (conflictsNext3moOnly && weekStartISO > threeMonthCutoffISO) continue;
      const weekDate = new Date(c.WeekStartDate);
      const weekLabel = weekDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      items.push({
        type: 'conflict', severity: 'high',
        title: `${c.StaffName} — ${c.Level}`,
        text: `Overbooked by ${c.OverageHours}h week of ${weekLabel} (${c.TotalScheduled}h scheduled / ${c.CapacityHours}h capacity)`,
        staffId: c.StaffId, dept: c.DepartmentCode || 'all', weekStart: weekStartISO,
      });
    }
    const order = { high: 0, medium: 1, low: 2 };
    return items.sort((a, b) => {
      const sevDiff = order[a.severity] - order[b.severity];
      if (sevDiff !== 0) return sevDiff;
      const dateA = a.weekStart || a.dueDate || '9999';
      const dateB = b.weekStart || b.dueDate || '9999';
      return dateA < dateB ? -1 : dateA > dateB ? 1 : 0;
    });
  }, [myJobs, conflicts, schedule, wipByJob, conflictsNext3moOnly]);

  const visibleAlerts = alertFilter === 'all' ? alerts : alerts.filter(a => a.type === alertFilter);

  const upcoming = useMemo(() => {
    const now = new Date();
    const weekBuckets = Array.from({ length: 4 }, (_, i) => {
      const start = new Date(now);
      start.setDate(start.getDate() - start.getDay() + 1 + i * 7);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 4);
      return { start, end, label: `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`, jobs: [] };
    });
    for (const j of myJobs) {
      if (!j.Deadline) continue;
      const due = new Date(j.Deadline.slice(0, 10) + 'T12:00:00');
      for (const wk of weekBuckets) {
        if (due >= wk.start && due <= wk.end) { wk.jobs.push(j); break; }
      }
    }
    return weekBuckets;
  }, [myJobs]);

  const goScheduleJob = (job) => {
    if (!job) return;
    sessionStorage.setItem('pendingAssignment', JSON.stringify({
      jobId: job.JobId, jobName: job.Name, clientName: job.ClientName,
      staffIds: meStaffId ? [meStaffId] : [], staffNames: [],
      suggestedHours: Math.max(0, (job.BudgetHours || 0) - (job.scheduledHrs || 0)) || 40,
    }));
    navigate('/schedule/grid');
  };

  return (
    <div className="sched-mgr">
      <SchedStatBar items={[
        { value: stats.total, label: 'Total Jobs', color: BRAND.deepTeal },
        { value: stats.full, label: 'Fully Scheduled', color: BRAND.brightGreen },
        { value: stats.needs, label: 'Needs Scheduling', color: '#F59E0B' },
        { value: stats.overbudget, label: 'Over Budget', color: '#DC2626' },
        { value: stats.nobudget, label: 'No Budget', color: '#6B7280' },
      ]} />

      <div className="sched-mgr-overview">
        <div className="sched-mgr-overview-grid" style={{ gridTemplateColumns: myJobs.length > 0 ? '1fr 1fr 1fr' : '1fr 1fr' }}>

          {myJobs.length > 0 && (
            <div className="sched-mgr-overview-card">
              <div className="sched-mgr-overview-card-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>
                  Needs Attention
                  {alerts.length > 0 && <span className="sched-tab-badge sched-tab-badge--red" style={{ marginLeft: 6 }}>{alerts.length}</span>}
                </span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 500, color: '#6B7280', cursor: 'pointer' }}
                  title="Hide overbooking more than 3 months out (e.g. busy-season weeks you can't act on yet)">
                  <input type="checkbox" checked={conflictsNext3moOnly}
                    onChange={e => { setConflictsNext3moOnly(e.target.checked); setShowAllAlerts(false); }} />
                  Next 3 months
                </label>
              </div>
              {alerts.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                  {[['all', 'All'], ['conflict', 'Overbooked'], ['budget', 'Over Budget'], ['due', 'Due Soon']].map(([key, label]) => {
                    const count = key === 'all' ? alerts.length : alerts.filter(a => a.type === key).length;
                    return (
                      <button key={key}
                        className={`sched-mgr-pill${alertFilter === key ? ' sched-mgr-pill--active' : ''}`}
                        onClick={() => { setAlertFilter(key); setShowAllAlerts(false); }}>
                        {label} ({count})
                      </button>
                    );
                  })}
                </div>
              )}
              {alerts.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>
                  <div style={{ fontSize: 24, marginBottom: 4 }}>&#9989;</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>All clear — nothing needs attention.</div>
                </div>
              ) : (
                <div className="sched-mgr-overview-alerts">
                  {visibleAlerts.length === 0 && (
                    <div style={{ textAlign: 'center', padding: 16, color: '#9CA3AF', fontSize: 13 }}>No alerts in this category.</div>
                  )}
                  {(showAllAlerts ? visibleAlerts : visibleAlerts.slice(0, 5)).map((a, i) => (
                    <div key={i} className="sched-alert" style={{ border: '1px solid', borderColor: a.severity === 'high' ? '#FECACA' : a.severity === 'medium' ? '#FED7AA' : '#E5E7EB' }}>
                      <div className="sched-alert-severity" style={{ background: a.severity === 'high' ? '#EF4444' : a.severity === 'medium' ? '#F59E0B' : '#9CA3AF' }} />
                      <span className="sched-alert-icon">
                        {a.type === 'due' ? '⏰' : a.type === 'conflict' ? '⚠️' : '💰'}
                      </span>
                      <div className="sched-alert-body">
                        <div className="sched-alert-title">{a.title}</div>
                        <div className="sched-alert-text">{a.text}</div>
                      </div>
                      <div className="sched-alert-actions">
                        {a.type === 'due' && (
                          <button className="sched-alert-btn" onClick={() => {
                            const job = enrichedJobs.find(j => j.JobId === a.jobId);
                            if (job) navigate(`/schedule/suggestions/${job.JobId}${job.Deadline ? `?we=${String(job.Deadline).slice(0, 10)}` : ''}`);
                          }}>Find Staff</button>
                        )}
                        {a.type === 'budget' && (
                          <button className="sched-alert-btn" onClick={() => {
                            setActiveView('myjobs');
                            setSelectedJobId(a.jobId);
                            setTimeout(() => {
                              const row = document.querySelector(`tr[data-job-id="${a.jobId}"]`);
                              if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }, 300);
                          }}>View Job</button>
                        )}
                        {a.type === 'conflict' && (
                          <button className="sched-alert-btn" onClick={() => {
                            const currentMon = new Date();
                            currentMon.setDate(currentMon.getDate() - ((currentMon.getDay() + 6) % 7));
                            currentMon.setHours(0, 0, 0, 0);
                            const conflictMon = new Date(a.weekStart + 'T12:00:00');
                            const weeksAhead = Math.round((conflictMon - currentMon) / (7 * 86400000));
                            sessionStorage.setItem('gridViewContext', JSON.stringify({
                              tab: 'alerts',
                              deptFilter: a.dept || 'all',
                              clearStaffFilter: true,
                              highlightConflictKey: `${a.staffId}-${a.weekStart}`,
                              weekOffset: Math.max(0, weeksAhead - 1),
                              zoom: 5,
                            }));
                            navigate('/schedule/grid');
                          }}>Resolve</button>
                        )}
                      </div>
                    </div>
                  ))}
                  {visibleAlerts.length > 5 && !showAllAlerts && (
                    <div style={{ textAlign: 'center', fontSize: 12, color: '#6B7280', marginTop: 6 }}>
                      <button style={{ background: 'none', border: 'none', color: '#356371', fontWeight: 600, cursor: 'pointer', fontSize: 12 }}
                        onClick={() => setShowAllAlerts(true)}>
                        View all {visibleAlerts.length} alerts &rarr;
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Upcoming 4 Weeks */}
          <div className="sched-mgr-overview-card">
            <div className="sched-mgr-overview-card-title">Upcoming 4 Weeks</div>
            <div className="sched-mgr-overview-weeks">
              {upcoming.map((wk, wi) => (
                <div key={wi} className="sched-mgr-overview-week">
                  <div className="sched-mgr-overview-week-header">
                    <span className="sched-mgr-overview-week-label">{wk.label}</span>
                    <span className="sched-mgr-overview-week-count">{wk.jobs.length} job{wk.jobs.length !== 1 ? 's' : ''} due</span>
                  </div>
                  {wk.jobs.length === 0 ? (
                    <div style={{ fontSize: 11, color: '#D1D5DB', padding: '4px 0' }}>&mdash; No jobs due this week</div>
                  ) : (
                    wk.jobs.slice(0, 4).map(j => {
                      const tc = JOB_TYPE_COLORS[j.ServiceDesc] || JOB_TYPE_COLORS[j.JobType] || { bg: '#E5E7EB', text: '#374151' };
                      return (
                        <div key={j.JobId} className="sched-mgr-overview-week-job"
                          onClick={() => goScheduleJob(j)}
                          style={{ cursor: 'pointer' }}>
                          <span className="sched-type-tag" style={{ background: tc.bg, color: tc.text, fontSize: 10, padding: '1px 6px' }}>
                            {j.ServiceDesc || j.JobType || ''}
                          </span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>{j.ClientName}</span>
                          <span style={{ fontSize: 11, color: '#9CA3AF' }}>{Math.round(j.scheduledHrs)}h</span>
                        </div>
                      );
                    })
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Recent Notifications */}
          <div className="sched-mgr-overview-card">
            <div className="sched-mgr-overview-card-title">
              Recent Notifications
              {recentNotifs.filter(n => !n.IsRead).length > 0 && (
                <span className="sched-tab-badge sched-tab-badge--blue" style={{ marginLeft: 6 }}>{recentNotifs.filter(n => !n.IsRead).length}</span>
              )}
            </div>
            {recentNotifs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>&#128276;</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>No recent notifications.</div>
                <div style={{ fontSize: 11, marginTop: 4 }}>Notifications appear when your schedule changes.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {recentNotifs.map(n => {
                  const colors = { assignment: '#3B82F6', move: '#F59E0B', removal: '#EF4444', conflict: '#EF4444' };
                  return (
                    <div key={n.NotifId} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '6px 8px', background: n.IsRead ? '#fff' : '#F0F9FF', borderRadius: 5, border: '1px solid #F3F4F6', cursor: n.RelatedStaffId || n.RecipientId ? 'pointer' : 'default' }}
                      onClick={() => {
                        const ids = [n.RecipientId, n.RelatedStaffId].filter(Boolean);
                        if (ids.length > 0) {
                          sessionStorage.setItem('gridViewContext', JSON.stringify({ staffIds: [...new Set(ids)], message: `Showing: ${n.Title}` }));
                          navigate('/schedule/grid');
                        }
                      }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: colors[n.NotifType] || '#9CA3AF', marginTop: 4, flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{n.Title}</div>
                        <div style={{ fontSize: 11, color: '#6B7280' }}>{n.Body}</div>
                        <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>
                          {n.ActorName && <>{n.ActorName} &middot; </>}
                          {new Date(n.CreatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </div>
                      </div>
                      {!n.IsRead && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3B82F6', flexShrink: 0, marginTop: 4 }} />}
                    </div>
                  );
                })}
                <div style={{ textAlign: 'center', marginTop: 4 }}>
                  <button style={{ background: 'none', border: 'none', color: '#356371', fontWeight: 600, cursor: 'pointer', fontSize: 12 }}
                    onClick={() => navigate('/schedule/notifications')}>
                    View all notifications &rarr;
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
